import http from '../../services/http-common';
import {
  PdfGrid, PdfPen, PdfBitmap, PdfBrushes, PdfDocument,
  PdfColor, PdfSolidBrush, PdfFontStyle, PdfPageSize,
  PdfStringFormat, PdfTextAlignment, PdfStandardFont,
  PdfVerticalAlignment, PdfFontFamily, PdfLayoutType, PdfLayoutFormat
} from '@syncfusion/ej2-pdf-export';
import moment from 'moment';

// ─────────────────────────────────────────────────────────────────────────────
// SaleInvoiceServicePreivewG
// Generates a Sales Tax Invoice PDF that exactly replicates the new
// two-section header design (Seller Info | Invoice Meta, Buyer Info | QR Code),
// the 10-column line-item table, and the summary / signature footer.
// ─────────────────────────────────────────────────────────────────────────────
class SaleInvoiceServicePreivewG {

  // ── Font factory helpers ────────────────────────────────────────────────────
  _font(size, bold = false) {
    return new PdfStandardFont(
      PdfFontFamily.Helvetica,
      size,
      bold ? PdfFontStyle.Bold : PdfFontStyle.Regular,
    );
  }
   convertNumberToWords(num) {
    const units = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen",
      "Sixteen", "Seventeen", "Eighteen", "Nineteen"
    ];
    const tens = [
      "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy",
      "Eighty", "Ninety"
    ];
    const scales = [
      { value: 10000000, name: "Crore" },
      { value: 100000, name: "Lakh" },
      { value: 1000, name: "Thousand" },
      { value: 100, name: "Hundred" }
    ];
  
    function numberToWords(n) {
      if (n === 0) return "Zero";
      if (n < 20) return units[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + units[n % 10] : "");
  
      for (let scale of scales) {
        if (n >= scale.value) {
          const quotient = Math.floor(n / scale.value);
          const remainder = n % scale.value;
          return (
            numberToWords(quotient) +
            " " +
            scale.name +
            (remainder ? " " + numberToWords(remainder) : "")
          );
        }
      }
      return "";
    }
  
    const whole = Math.floor(num);
    const fraction = Math.round((num - whole) * 100);
  
    let result = numberToWords(whole) + " Rupees";
    result += " and " + numberToWords(fraction) + " Paise";
  
    return result;
  }
  // ── Brush factory ───────────────────────────────────────────────────────────
  _rgb(r, g, b) { return new PdfSolidBrush(new PdfColor(r, g, b)); }

  // ── Pen factory ─────────────────────────────────────────────────────────────
  _pen(r, g, b, w = 1) { return new PdfPen(new PdfColor(r, g, b), w); }

  // ── Converts a Blob/File to a base-64 string (no data-URL prefix) ───────────
  _blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror   = reject;
      reader.readAsDataURL(blob);
    });
  }

  // ── Fetches a URL and returns a base-64 string ──────────────────────────────
  async _fetchBase64(url) {
    const res  = await fetch(url);
    if (!res.ok) throw new Error(`Fetch failed: ${res.statusText} (${url})`);
    const blob = await res.blob();
    return this._blobToBase64(blob);
  }

  // ── Draws left-aligned wrapped text; returns the Y of the last line drawn ───
  wrapText(
    graphics,
    text,
    font,
    brush,
    x,
    y,
    maxWidth,
    lineHeight,
    align = 'left'
  ) {
    const words = String(text ?? '').split(' ');
    let line = '';
    let yOffset = y;
  
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const testWidth = font.measureString(testLine).width;
  
      if (testWidth > maxWidth && i > 0) {
  
        // line width
        const lineWidth = font.measureString(line.trim()).width;
  
        // alignment
        let drawX = x;
  
        if (align === 'center') {
          drawX = x + (maxWidth - lineWidth) / 2;
        }
  
        if (align === 'right') {
          drawX = x + (maxWidth - lineWidth);
        }
  
        graphics.drawString(
          line.trim(),
          font,
          null,
          brush,
          drawX,
          yOffset
        );
  
        line = words[i] + ' ';
        yOffset += lineHeight;
  
      } else {
        line = testLine;
      }
    }
  
    // draw last line
    if (line.trim()) {
  
      const lineWidth = font.measureString(line.trim()).width;
  
      let drawX = x;
  
      if (align === 'center') {
        drawX = x + (maxWidth - lineWidth) / 2;
      }
  
      if (align === 'right') {
        drawX = x + (maxWidth - lineWidth);
      }
  
      graphics.drawString(
        line.trim(),
        font,
        null,
        brush,
        drawX,
        yOffset
      );
    }
  
    return yOffset;
  }

  // ── Draws "Label:   Value" using two fonts, returns nothing ─────────────────
  _drawField(graphics, label, value, lFont, vFont, brush, lx, vx, y) {
    graphics.drawString(`${label}:`, lFont, null, brush, lx, y);
    graphics.drawString(String(value ?? ''), vFont, null, brush, vx, y);
  }

  // ── Right-aligns text inside a box ─────────────────────────────────────────
  _drawRightAligned(graphics, text, font, brush, boxX, boxW, pad, y) {
    const tw  = font.measureString(text).width;
    graphics.drawString(text, font, null, brush, boxX + boxW - pad - tw, y);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Pre-load every image asset before any drawing begins so the PDF draw
  // calls remain synchronous and positional tracking stays accurate.
  // ═══════════════════════════════════════════════════════════════════════════
  async _loadImages(invoiceDetails, qrBlob) {
    const images = {};

    // QR code (the "blob" argument coming from the caller)
    if (qrBlob) {
      const qrUrl    = URL.createObjectURL(qrBlob);
      images.qr      = new PdfBitmap(await this._fetchBase64(qrUrl));
    }

    // FBR logo (served as a static asset)
    try {
      images.fbrLogo = new PdfBitmap(await this._fetchBase64('/fbr-img.jpg'));
    } catch (_) { /* non-critical */ }

    // Seller logo (optional Blob on invoiceDetails)
    if (invoiceDetails.sellerLogo && invoiceDetails.sellerLogo !== '') {
      try {
        const logoUrl  = URL.createObjectURL(invoiceDetails.sellerLogo);
        images.sellerLogo = new PdfBitmap(await this._fetchBase64(logoUrl));
      } catch (_) { /* non-critical */ }
    }

    return images;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Main entry-point – mirrors the existing public API exactly
  // ═══════════════════════════════════════════════════════════════════════════
  async SalesTaxInvoiceReport(invoiceDetails, invoiceItems, blob, type = 'download') {

    // ── 0. Pre-load all images ────────────────────────────────────────────────
    const images = await this._loadImages(invoiceDetails, blob);

    // ── 1. Document & page setup ──────────────────────────────────────────────
    const doc = new PdfDocument();
    doc.pageSettings.size          = PdfPageSize.a4;
    doc.pageSettings.margins.all   = 30;
    doc.pageSettings.margins.bottom = 10;

    const page      = doc.pages.add();
    let   graphics  = page.graphics;
    const pageW     = page.getClientSize().width;   // ~565 pt (A4 – margins)
    const pageH     = page.getClientSize().height;

    // ── 2. Font palette ───────────────────────────────────────────────────────
    const fTitle       = this._font(16, true);   // "SALES TAX INVOICE"
    const fSection     = this._font(10,  true);   // "Seller Information" etc.
    const fLabel       = this._font(9,  true);   // field labels
    const footLabel       = this._font(7.8,  true);   // field labels
    const fValue       = this._font(8,  false);  // field values
    const fBold8       = this._font(9,  true);
    const fBold9       = this._font(9,  true);
    const fFooter      = this._font(7,  false);
    const fSummaryLbl  = this._font(7.5, false);
    const fSummaryTot  = this._font(9,  true);
    const fTblHdr      = this._font(6.5, true);
    const fTblBody     = this._font(6.5, false);
    const fTblTotals   = this._font(6.5, true);

    // ── 3. Brush / pen palette ────────────────────────────────────────────────
    const black      = this._rgb(0,   0,   0);
    const white      = this._rgb(255, 255, 255);
    const gray       = this._rgb(120, 120, 120);
    const lightGray  = this._rgb(240, 240, 240);
    const tblHdrBg   = this._rgb(30,  30,  30);   // near-black header

    const penBorder  = this._pen(160, 160, 160, 1);   // info-box border
    const penDivider = this._pen(200, 200, 200, 0.4);   // inner separator
    const penThick   = this._pen(0,   0,   0,  0.9);   // sig line / grand total
    // const penLight   = this._pen(220, 220, 220, 0.3);   // table internal lines
    const penLight = this._pen(190,190,190,0.45);
    // ─────────────────────────────────────────────────────────────────────────
    // SECTION A – TITLE ROW
    // ─────────────────────────────────────────────────────────────────────────
    const titleText = 'SALES TAX INVOICE';
    const titleW    = fTitle.measureString(titleText).width;
    graphics.drawString(titleText, fTitle, null, black, (pageW - titleW) / 2, 4);

    // FBR logo – top-right corner (drawn on first page graphics directly)
    if (images.fbrLogo) {
      graphics.drawImage(images.fbrLogo, pageW - 48, 0, 40, 38);
    }

    // Seller logo – top-left (scaled, max 80×28)
    // if (images.sellerLogo) {
    //   const lw = images.sellerLogo.width;
    //   const lh = images.sellerLogo.height;
    //   const sc = Math.min(80 / lw, 28 / lh);
    //   graphics.drawImage(images.sellerLogo, 0, 0, lw * sc, lh * sc);
    // }

    let curY = 50;  // Y cursor (below title)

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION B – SELLER INFO BOX  +  INVOICE META BOX
    // Layout: |← sellerBoxW (58 %) →|  5 pt gap  |← metaBoxW →|
    // ─────────────────────────────────────────────────────────────────────────
    const GAP        = 8;
    const sellerBoxW = Math.floor(pageW * 0.70);
    const metaBoxX   = sellerBoxW + GAP;
    const metaBoxW   = pageW - metaBoxX;

    const LINE_H = 11;
const PAD    = 6;

    // We measure height by drawing content first, then draw the border.
    // Helper: draws a section header label + underline, advances inY by LINE_H
    const drawSectionHeader = (g, label, x, w, inY,drawline = true) => {
      g.drawString(label, fSection, null, black, x + PAD, inY);
      inY += LINE_H;
      if(drawline == true){
      g.drawLine(penDivider, x, inY - 2, x + w, inY - 2);
      }
      return inY;
    };

    // ── Seller box content ────────────────────────────────────────────────────
    let sY = curY + PAD;
    sY = drawSectionHeader(graphics, 'Seller Information', 0, sellerBoxW, sY,false);
    sY += LINE_H;
    const lx = PAD;
    const vx = PAD + 76;
    const vW = sellerBoxW - vx - PAD;

    this._drawField(graphics, 'Company',       String(invoiceDetails?.sellerName    ?? '').toUpperCase(), fLabel, fValue, black, lx, vx, sY);
    sY += LINE_H;

    graphics.drawString('Address:', fLabel, null, black, lx, sY);
    sY = this.wrapText(graphics, String(invoiceDetails?.sellerAddress ?? '').toUpperCase(), fValue, black, vx, sY, vW, LINE_H) + LINE_H;

    const sellerCityProv = `${invoiceDetails?.sellerCity ?? '-'} / ${invoiceDetails?.sellerRegion ?? ''}`.toUpperCase();
    this._drawField(graphics, 'City/Province', sellerCityProv,                                                              fLabel, fValue, black, lx, vx, sY);
    sY += LINE_H;

    this._drawField(graphics, 'NTN/CNIC',  String(invoiceDetails?.sellerNTN  ?? ''), fLabel, fValue, black, lx, vx, sY);
    sY += LINE_H;

    // Use long NTN/CNIC reference if it has ≥7 chars, else fallback to sellerSTRN
    const sellerStrn = (invoiceDetails?.sellerNTNCNICReferenceNo?.length >= 7)
      ? invoiceDetails.sellerNTNCNICReferenceNo
      : (invoiceDetails?.sellerSTRN ?? '');
    this._drawField(graphics, 'STRN', String(sellerStrn).toUpperCase(), fLabel, fValue, black, lx, vx, sY);
    sY += PAD;

    const sellerBoxH = sY - curY + LINE_H;

    // Draw seller box border
    graphics.drawRectangle(penBorder, 0, curY, sellerBoxW, sellerBoxH);

    // ── Invoice meta box (Invoice Date + Invoice No) ──────────────────────────
   

    const fields = [
      {
        label: 'Invoice Date',
        value: moment(invoiceDetails.date).format('DD/MM/YYYY')
      },
      {
        label: 'Invoice No',
        value: String(invoiceDetails.SaleInvoiceID ?? '')
      }
    ];
    
    let mY = curY + PAD;
    
    const mlx = metaBoxX + PAD;
    const mvx = metaBoxX + PAD + 60;
    
    // Draw fields
    fields.forEach(field => {
      this._drawField(
        graphics,
        field.label,
        field.value,
        fLabel,
        fValue,
        black,
        mlx,
        mvx,
        mY
      );
    
      mY += LINE_H;
    });
    
    // Dynamic height
    const DateBoxH =
      (PAD * 2) +
      (fields.length * LINE_H)
      
    
    // Draw rectangle
    graphics.drawRectangle(
      penBorder,
      metaBoxX,
      curY,
      metaBoxW,
      DateBoxH
    );




    curY += sellerBoxH + GAP;

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION C – BUYER INFO BOX  +  QR CODE BOX
    // ─────────────────────────────────────────────────────────────────────────
    const buyerBoxW = sellerBoxW;   // same width as seller box
    const qrBoxX    = buyerBoxW + GAP;
    const qrBoxW    = metaBoxW;

    let bY = curY + PAD;
    bY = drawSectionHeader(graphics, 'Buyer Information', 0, buyerBoxW, bY,false);
    bY += LINE_H;
    const blx = PAD;
    const bvx = PAD + 76;
    const bvW = buyerBoxW - bvx - PAD;

    this._drawField(graphics, 'Customer Name', String(invoiceDetails?.buyerName    ?? '').toUpperCase(), fLabel, fValue, black, blx, bvx, bY);
    bY += LINE_H;

    graphics.drawString('Address:', fLabel, null, black, blx, bY);
    bY = this.wrapText(graphics, String(invoiceDetails?.buyerAddress ?? '').toUpperCase(), fValue, black, bvx, bY, bvW, LINE_H) + LINE_H;

    const buyerCityProv = `${invoiceDetails?.buyerCity ?? '-'} / ${invoiceDetails?.SaleInvoiceJSONbuyerProvince ?? ''}`.toUpperCase();
    this._drawField(graphics, 'City/Province', buyerCityProv,                                                               fLabel, fValue, black, blx, bvx, bY);
    bY += LINE_H;

    this._drawField(graphics, 'NTN/CNIC', String(invoiceDetails?.buyerNTN  ?? ''), fLabel, fValue, black, blx, bvx, bY);
    bY += LINE_H;

    this._drawField(graphics, 'STRN',     String(invoiceDetails?.buyerSTRN ?? ''), fLabel, fValue, black, blx, bvx, bY);
    bY += LINE_H;

    this._drawField(graphics, 'Registered', String((invoiceDetails?.buyerType).toUpperCase() == "Registered" ? 'YES' : 'NO'), fLabel, fValue, black, blx, bvx, bY);
    bY += PAD;

    const buyerBoxH = bY - curY + LINE_H ;

    graphics.drawRectangle(penBorder, 0,      curY, buyerBoxW, buyerBoxH);
    // graphics.drawRectangle(penBorder, qrBoxX, curY, qrBoxW,   buyerBoxH);

    // ── QR code + FBR invoice label inside QR box ────────────────────────────
    if (images.qr) {
      const qrSize  = 150
      const qrDrawX = buyerBoxW + 40;
      const qrDrawY = curY   - LINE_H;
      graphics.drawImage(images.qr, qrDrawX, qrDrawY, qrSize, qrSize);
    
    // Text
  const fbrInvLabel = `FBR Invoice #`;
  const fbrInvVal   = String(invoiceDetails?.invoiceNo ?? '-');

  // Center text according to QR width
  const labelWidth = fLabel.measureString(fbrInvLabel).width;
  const valueWidth = fValue.measureString(fbrInvVal).width;

  const labelX = qrDrawX + (95 - labelWidth) / 2;
  const valueX = qrDrawX + (90 - valueWidth) / 2;

  graphics.drawString(
    fbrInvLabel,
    fLabel,
    null,
    black,
    labelX,
    qrDrawY + 70 + LINE_H
  );

  graphics.drawString(
    fbrInvVal,
    fValue,
    null,
    black,
    valueX,
    qrDrawY + 80 + LINE_H 
  );

  }

    curY += buyerBoxH + 6;

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION D – LINE-ITEM TABLE
    // Columns: S.NO | Items & Description | HS Code | UOM | Qty | Unit Price
    //          | Value Excl. ST | Rate of ST | Amount of ST | Value Incl. ST
    // ─────────────────────────────────────────────────────────────────────────

    // Column definitions: key maps to invoiceItems field; min = base width in pt
    // NOTE: 'salesTaxRate' is a NEW field – see field mapping notes at bottom.
    // const COLS = [
    //   { key: 'srNo',         label: 'S.NO',                              min: 16 },
    //   { key: 'description',  label: 'Items & Description',               min: 88, align: PdfTextAlignment.Left },
    //   { key: 'hsCode',       label: 'HS Code',                           min: 40 },
    //   { key: 'uoM',          label: 'UOM',                               min: 20 },
    //   { key: 'quantity',     label: 'Qty',                               min: 28 },
    //   { key: 'rate',         label: 'Unit Price',                        min: 30 },
    //   { key: 'amount',       label: 'Value\nExcl.\nSales Tax',           min: 42 },
    //   { key: 'salesTaxRate', label: 'Rate\nof\nSales\nTax',             min: 26 },
    //   { key: 'salesTax',     label: 'Amount\nof Sales\nTax',            min: 38 },
    //   { key: 'totalInclTax', label: 'Value\nIncluding\nSales Tax',      min: 44 },
    // ];
  // ==============================
// TABLE CONFIGURATION
// ==============================

// Full usable table width


const tableWidth = pageW;

// ==============================
// NUMBER FORMATTER
// ==============================

const formatNumber = (num) => {

  const n = Number(num ?? 0);

  const hasDecimal = n % 1 !== 0;

  return n.toLocaleString('en-US', {
    minimumFractionDigits:2,
    maximumFractionDigits: 2
  });
};

// ==============================
// COLUMNS
// ==============================

const COLS = [
  {
    key: 'srNo',
    label: 'Sr No.',
    percent: 5,
    visible: true
  },

  {
    key: 'description',
    label: 'Items & Description',
    percent: 20,
    align: PdfTextAlignment.Left,
    visible: true
  },

  {
    key: 'hsCode',
    label: 'HS Code',
    percent: 9,
    visible: true
  },

  {
    key: 'uoM',
    label: 'UOM',
    percent: 7,
    visible: true
  },

  {
    key: 'quantity',
    label: 'Qty',
    percent: 9,
    visible: true
  },

  {
    key: 'unitPrice',
    label: 'Unit\nPrice',
    percent: 9,
    visible: true
  },

  {
    key: 'amount',
    label: 'Value\nExcl.\nSales Tax',
    percent: 12,
    visible: true
  },

  {
    key: 'rate',
    label: 'Rate\nof\nSales\nTax',
    percent: 7,
    visible: true
  },

  // NEW COLUMN
  {
    key: 'furtherTax',
    label: 'Further\nTax',
    percent: 8,
    visible: invoiceDetails.furtherTaxsVisible
  },

  {
    key: 'salesTax',
    label: 'Amount\nof\nSales\nTax',
    percent: 11,
    visible: true
  },

  {
    key: 'totalInclTax',
    label: 'Value\nIncluding\nSales Tax',
    percent: 12,
    visible: true
  },
];

// ==============================
// FILTER VISIBLE COLUMNS
// ==============================

const visibleCols = COLS.filter(
  col => col.visible !== false
);

// ==============================
// AUTO WIDTH DISTRIBUTION
// ==============================

const totalVisiblePercent = visibleCols.reduce(
  (sum, col) => sum + col.percent,
  0
);

visibleCols.forEach(col => {

  col.width =
    (tableWidth * col.percent) /
    totalVisiblePercent;
});

// ==============================
// COLORS
// ==============================

const borderPen = new PdfPen(
  new PdfColor(0, 0, 0),
  0.8
);

const headerBg = new PdfSolidBrush(
  new PdfColor(230, 230, 230)
);

const totalBg = new PdfSolidBrush(
  new PdfColor(245, 245, 245)
);

const whiteBrush = new PdfSolidBrush(
  new PdfColor(255, 255, 255)
);

const blackBrush = new PdfSolidBrush(
  new PdfColor(0, 0, 0)
);

// ==============================
// GRID
// ==============================

const pdfGrid = new PdfGrid();

pdfGrid.columns.add(visibleCols.length);

// SET WIDTHS

visibleCols.forEach((c, i) => {

  pdfGrid.columns
    .getColumn(i)
    .width = c.width;
});

// ==============================
// HEADER
// ==============================

const hdrRow = pdfGrid.headers.add(1)[0];

visibleCols.forEach((col, i) => {

  const cell = hdrRow.cells.getCell(i);

  cell.value = col.label;

  cell.style.font = new PdfStandardFont(
    PdfFontFamily.Helvetica,
    8.5,
    PdfFontStyle.Bold
  );

  cell.style.backgroundBrush = headerBg;

  cell.style.textBrush = blackBrush;

  cell.style.stringFormat = new PdfStringFormat(
    PdfTextAlignment.Center,
    PdfVerticalAlignment.Middle
  );

  cell.style.borders.all = borderPen;

  cell.style.cellPadding = {
    left: 2,
    right: 2,
    top: 5,
    bottom: 5,
  };
});

// HEADER HEIGHT

// hdrRow.height = 42;

// ==============================
// TOTALS
// ==============================

let totQty = 0;
let totExcl = 0;
let totFurtherTax = 0;
let totSalesTax = 0;
let totIncl = 0;

// ==============================
// DATA ROWS
// ==============================

invoiceItems.forEach((item, rowIndex) => {

  const row = pdfGrid.rows.addRow();

  visibleCols.forEach((col, i) => {

    const cell = row.cells.getCell(i);

    cell.style.font = new PdfStandardFont(
      PdfFontFamily.Helvetica,
      8
    );

    cell.style.backgroundBrush = whiteBrush;

    cell.style.textBrush = blackBrush;

    cell.style.borders.all = borderPen;

    cell.style.cellPadding = {
      left: 3,
      right: 3,
      top: 3,
      bottom: 3,
    };

    cell.style.stringFormat = new PdfStringFormat(
      col.align ?? PdfTextAlignment.Center,
      PdfVerticalAlignment.Middle
    );

    let val = item[col.key] ?? '';

    switch (col.key) {

      case 'srNo':

        val = rowIndex + 1;

        break;

      case 'quantity': {

        const q = Number(item.quantity ?? 0);

        totQty += q;

        val = formatNumber(q);

        break;
      }

      case 'unitPrice': {

        const quantity = Number(item.quantity ?? 0);

        const amount = Number(item.amount ?? 0);

        val = formatNumber(
          quantity !== 0
            ? amount / quantity
            : 0
        );

        break;
      }

      case 'rate': {

        // const r =
        //   item.salesTaxRate ??
        //   item.salesTaxPercent ??
        //   item.taxRate ??
        //   item.gstRate ??
        //   item.rate;

        val = item.rate
          ? `${item.rate}`
          : '';

        break;
      }

      case 'amount': {

        const a = Number(item.amount ?? 0);

        totExcl += a;

        val = formatNumber(a);

        break;
      }

      case 'furtherTax': {

        const ft = Number(
          item.furtherTax ?? 0
        );

        totFurtherTax += ft;

        val = formatNumber(ft);

        break;
      }

      case 'salesTax': {

        const t = Number(
          item.salesTax ?? 0
        );

        totSalesTax += t;

        val = formatNumber(t);

        break;
      }

      case 'totalInclTax': {

        const ti = Number(
          item.totalInclTax ?? 0
        );

        totIncl += ti;

        val = formatNumber(ti);

        break;
      }

      default:
        break;
    }

    cell.value = String(val);
  });
});

// ==============================
// TOTAL ROW
// ==============================

const totRow = pdfGrid.rows.addRow();

totRow.height = 24;

visibleCols.forEach((col, i) => {

  const cell = totRow.cells.getCell(i);

  cell.style.font = new PdfStandardFont(
    PdfFontFamily.Helvetica,
    8.5,
    PdfFontStyle.Bold
  );

  cell.style.backgroundBrush = totalBg;

  cell.style.textBrush = blackBrush;

  cell.style.borders.all = borderPen;

  cell.style.cellPadding = {
    left: 3,
    right: 3,
    top: 3,
    bottom: 3,
  };

  cell.style.stringFormat = new PdfStringFormat(
    col.align ?? PdfTextAlignment.Center,
    PdfVerticalAlignment.Middle
  );

  const fmtNum = (n) => formatNumber(n);

  switch (col.key) {

    case 'description':

      cell.value = 'Total';

      cell.style.stringFormat =
        new PdfStringFormat(
          PdfTextAlignment.Left,
          PdfVerticalAlignment.Middle
        );

      break;

    case 'quantity':

      cell.value = fmtNum(totQty);

      break;

    case 'amount':

      cell.value = fmtNum(totExcl);

      break;

    case 'furtherTax':

      cell.value = fmtNum(totFurtherTax);

      break;

    case 'salesTax':

      cell.value = fmtNum(totSalesTax);

      break;

    case 'totalInclTax':

      cell.value = fmtNum(totIncl);

      break;

    default:

      cell.value = '';

      break;
  }
});

// ==============================
// GRID STYLE
// ==============================

pdfGrid.style.cellSpacing = 0;

pdfGrid.style.borderPen = borderPen;

// ==============================
// DRAW GRID
// ==============================

const layoutFmt = new PdfLayoutFormat();

layoutFmt.layout = PdfLayoutType.Paginate;

const layoutResult = pdfGrid.draw(
  page,
  {
    x: 0,
    y: curY,
    width: tableWidth
  },
  layoutFmt
);

// ==============================
// COLUMN HIDE/SHOW EXAMPLES
// ==============================

// Hide HS Code
// COLS.find(x => x.key === 'hsCode').visible = false;

// Hide Further Tax
// COLS.find(x => x.key === 'furtherTax').visible = false;

// Show Again
// COLS.find(x => x.key === 'hsCode').visible = true;

    // Switch to whatever page the grid ended on (handles multi-page tables)
    let curPage = layoutResult.page;
    graphics    = curPage.graphics;
    curY        = layoutResult.bounds.y + layoutResult.bounds.height + 12;

    const curPageH = curPage.getClientSize().height;
    const curPageW = curPage.getClientSize().width;

    // If not enough vertical room for footer, start a new page
    if (curY + 200 > curPageH) {
      curPage  = doc.pages.add();
      graphics = curPage.graphics;
      curY     = 20;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION E – AMOUNT IN WORDS  +  INVOICE SUMMARY BOX
    // ─────────────────────────────────────────────────────────────────────────
    const SUMMARY_W   = 240;
    const SUMMARY_X   = curPageW - SUMMARY_W;
    const WORDS_W     = SUMMARY_X - 10;
    const SUMMARY_PAD = 8;
    const S_LINE      = 13;
    const summaryRows = [
      { label: 'Subtotal',   value: totExcl   ?? 0 },
      { label: 'Discount',   value: invoiceDetails.discount   ?? 0 },
      { label: 'SalesTax',  value:  totSalesTax  ?? 0 },
      { label: 'FurtherTax', value: totFurtherTax ?? 0 },
    ];
    // Amount in words (left side)
   // ==============================
// AMOUNT IN WORDS SECTION
// ==============================

// 1. Calculate total safely
let totalAmount = summaryRows.reduce((sum, row) => {
  return sum + Number(row.value || 0);
}, 0);

// 2. Convert number to words (IMPORTANT: pass number, not formatted string)
const amountInWords = String(this.convertNumberToWords(totalAmount) ?? '');

// ==============================
// BOX DIMENSIONS
// ==============================

const boxX = 0;
const boxY = curY ;
const boxW = WORDS_W;
const lineHeight = 15;

// rough estimation of height (adjust if your wrapText behaves differently)
const estimatedLines = Math.ceil(amountInWords.length / 85);
const boxH = (estimatedLines + 2) * lineHeight + 5;

// ==============================
// DRAW BORDER BOX
// ==============================


graphics.drawRectangle(penBorder,boxX, boxY, boxW, boxH);

// ==============================
// LABEL
// ==============================

graphics.drawString(
  'Amount in Words:',
  fBold8,
  null,
  black,
  boxX+4,
  curY+4
);

// ==============================
// WRAPPED TEXT
// ==============================

this.wrapText(
  graphics,
  amountInWords,
  // `Fourteen Lakh Twenty Thne Thousand Six Hundred Fifty Seven Rupees and
  // Two Paise`,
  fValue,
  black,
  boxX+4,
  curY + 20,
  WORDS_W,
  lineHeight
);

    // Summary rows
   

    

    const summaryH = SUMMARY_PAD
      + summaryRows.length * S_LINE
      + 3               // separator gap
      + S_LINE + 2  + S_LINE + 2      // grand total row
      + SUMMARY_PAD;

      graphics.drawRectangle(penBorder, SUMMARY_X, curY, SUMMARY_W, summaryH);
      let sYPos = curY + SUMMARY_PAD;
      graphics.drawString('INVOICE SUMMARY', fSection, null, black, SUMMARY_X + SUMMARY_PAD + 60, sYPos);
    graphics.drawLine(penThick, SUMMARY_X+4, sYPos +16, SUMMARY_X + SUMMARY_W-8, sYPos + 16);
    sYPos +=  20
    summaryRows.forEach(row => {
      const valStr = Number(row.value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      graphics.drawString(row.label, fSummaryLbl, null, black, SUMMARY_X + SUMMARY_PAD, sYPos);
      this._drawRightAligned(graphics, valStr, fSummaryLbl, black, SUMMARY_X, SUMMARY_W, SUMMARY_PAD, sYPos);
      sYPos += S_LINE;
    });

    // Grand total separator line + bold row
    const gtRowH = 18;

// Background color
// const gtBgBrush = new PdfSolidBrush(new PdfColor(230, 230, 230));
const gtBgBrush = new PdfSolidBrush(
  new PdfColor(186, 204, 150)
); 
// change RGB as needed

// Draw background rectangle
graphics.drawRectangle(
  gtBgBrush,
  SUMMARY_X  +4,
  sYPos + 1,
  SUMMARY_W - 10,
  gtRowH
);

    graphics.drawLine(penThick, SUMMARY_X + 4, sYPos , SUMMARY_X + SUMMARY_W -8 , sYPos );
    const gtVal = Number(invoiceDetails.grandTotal ?? 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    graphics.drawString('Grand Total', fSummaryTot, null, black, SUMMARY_X + SUMMARY_PAD, sYPos +4);
    this._drawRightAligned(graphics, gtVal, fSummaryTot, black, SUMMARY_X, SUMMARY_W, SUMMARY_PAD, sYPos +4);

    curY += summaryH + 44;

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION F – SIGNATURE LINES
    // ─────────────────────────────────────────────────────────────────────────
    if (curY + 60 > curPageH) {
      curPage  = doc.pages.add();
      graphics = curPage.graphics;
      curY     = 60;
    }

    // const sigLineW  = 160;
    // const buyerSigX = 10;
    // const sellSigX  = curPageW - sigLineW - 10;
    
    // // ================= BUYER =================
    // graphics.drawLine(
    //   penThick,
    //   buyerSigX,
    //   curY,
    //   buyerSigX + sigLineW,
    //   curY
    // );
    
    // const buyerName = String(invoiceDetails?.buyerName ?? '').toUpperCase();
    
    // // center text inside line width
    // const buyerTextW = footLabel.measureString(buyerName).width;
    
    // this.wrapText(
    //   graphics,
    //   buyerName,
    //   footLabel,
    //   black,
    //   buyerSigX,
    //   curY + 4,
    //   sigLineW,
    //   10,
    //   'center'
    // );
    
    // // ================= SELLER =================
    // graphics.drawLine(
    //   penThick,
    //   sellSigX,
    //   curY,
    //   sellSigX + sigLineW,
    //   curY
    // );
    
    // const sellerName = String(invoiceDetails?.sellerName ?? '').toUpperCase();
    
    // const sellerTextW = footLabel.measureString(sellerName).width;
    
    // this.wrapText(
    //   graphics,
    //   sellerName,
    //   footLabel,
    //   black,
    //   sellSigX,
    //   curY + 4,
    //   sigLineW,
    //   10,
    //   'center'
    // );

    curY += 22;

   // ===== footer position from page bottom =====
const footerBottomPadding = 18;
const footerLineGap = 8;
const penLightGray = new PdfPen(
  new PdfColor(200, 200, 200),
  0.5
);
const footerTextY =
  page.getClientSize().height - footerBottomPadding;

const footerLineY =
footerTextY - footerLineGap;




const pwrText1 =
  `THIS IS A SYSTEM GENERATED DIGITAL INVOICE. NO SIGNATURE REQUIRED
  `;

const pwrW1 = fFooter.measureString(pwrText1).width;

graphics.drawString(
  pwrText1,
  fFooter,
  null,
  black,
  (curPageW - pwrW1) / 2,
  footerTextY - 30
);

// ===== separator line =====
graphics.drawLine(
  penLightGray,
  10,
  footerLineY,
  curPageW - 10,
  footerLineY
);

// ===== centered footer text =====
const pwrText =
localStorage.getItem("CompanyPoweredBy") ? (localStorage.getItem("CompanyPoweredBy")).toUpperCase() :""
  // 'POWERED BY AA Consultant IT Solution - 021-36991205 - www.aaconsultant.com.pk';

const pwrW = fFooter.measureString(pwrText).width;

graphics.drawString(
  pwrText,
  fFooter,
  null,
  gray,
  (curPageW - pwrW) / 2,
  footerTextY
);

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION H – SAVE / RETURN
    // ─────────────────────────────────────────────────────────────────────────
    if (type === 'save') {
      const pdfBlob = await doc.save();
      doc.destroy();
      return pdfBlob;
    } else {
      doc.save('SalesTaxInvoice.pdf');
      doc.destroy();
    }
  }
}

export default new SaleInvoiceServicePreivewG();

// ═════════════════════════════════════════════════════════════════════════════
// FIELD MAPPING NOTES
// ═════════════════════════════════════════════════════════════════════════════
//
// All fields below are read from the same invoiceDetails / invoiceItems objects
// used by the previous report.  No API or data-flow changes are required for
// existing fields.  Missing fields are noted with [NEW] / [MISSING].
//
// ─── HEADER BLOCKS ───────────────────────────────────────────────────────────
// invoiceDetails.sellerName            → "Company"  (seller box)
// invoiceDetails.sellerAddress         → "Address"  (seller box, multi-line)
// invoiceDetails.sellerCity    [NEW]   → left part of "City/Province" for seller
//   ↳ Suggested source: add `sellerCity` to the seller data object (e.g. from
//     the address record).  Falls back to '-' if absent.
// invoiceDetails.sellerRegion          → right part of "City/Province" for seller
// invoiceDetails.sellerNTN             → "NTN"
// invoiceDetails.sellerNTNCNICReferenceNo / sellerSTRN → "STRN"
//
// invoiceDetails.date                  → "Invoice Date" (formatted DD/MM/YYYY)
// invoiceDetails.SaleInvoiceID         → "Invoice No"
//
// invoiceDetails.buyerName             → "Customer Name"
// invoiceDetails.buyerAddress          → "Address"  (multi-line)
// invoiceDetails.buyerCity    [NEW]    → left part of "City/Province" for buyer
//   ↳ Same as sellerCity – add to buyer record.  Falls back to '-'.
// invoiceDetails.SaleInvoiceJSONbuyerProvince → right part of "City/Province"
// invoiceDetails.buyerNTN              → "NTN/CNIC"
// invoiceDetails.buyerSTRN             → "STRN"
// invoiceDetails.buyerType             → "Registered"  (e.g. "True" / "False")
// invoiceDetails.invoiceNo             → FBR Invoice #  (shown below QR code)
//
// ─── TABLE COLUMNS ───────────────────────────────────────────────────────────
// item.srNo          → S.NO
// item.description   → Items & Description
// item.hsCode        → HS Code
// item.uoM           → UOM
// item.quantity      → Qty
// item.rate          → Unit Price
// item.amount        → Value Excl. Sales Tax
// item.salesTaxRate  [NEW/MISSING] → Rate of Sales Tax (e.g. 18.00%)
//   ↳ Field tried in priority order: salesTaxRate → salesTaxPercent → taxRate → gstRate
//   ↳ Add one of these fields to the line-item data returned by your API.
//     Alternatively it can be computed as: (item.salesTax / item.amount * 100)
// item.salesTax      → Amount of Sales Tax
// item.totalInclTax  → Value Including Sales Tax
//
// ─── SUMMARY BOX ─────────────────────────────────────────────────────────────
// invoiceDetails.subTotal              → Subtotal
// invoiceDetails.discount              → Discount
// invoiceDetails.salesTax              → SalesTax
// invoiceDetails.furtherTax            → FurtherTax
// invoiceDetails.extraTax              → Extra Tax  (shown only when > 0)
// invoiceDetails.fedPayable            → FED Payable (shown only when > 0)
// invoiceDetails.grandTotal            → Grand Total
// invoiceDetails.grandTotalInWords     → Amount in Words
//
// ─── OPTIONAL / EXISTING FIELDS PRESERVED ────────────────────────────────────
// invoiceDetails.ShowAdvanceIncomeTax  → controls Advance Tax row in summary
// invoiceDetails.AdvanceIncomeTaxDescription
// invoiceDetails.AdvanceIncomeTaxPercent
// invoiceDetails.TotalAdvanceIncomeTaxAmount
// invoiceDetails.SellerRemarks         → printed below the note line
// invoiceDetails.sellerLogo            → Blob for logo top-left
// blob (3rd arg)                       → QR code Blob (unchanged from original)